import React, { Component } from 'react';
import { Comment, Button, Form, Label, Popup, Header } from 'semantic-ui-react';
import { NARRATIVEBOARDCONFIG } from '../narrative-board-config';
import './narrative-board-comments.css';
import { addComment, editComment, deleteComment } from '../../../actions/narrative-board.actions';
import { NarrativeBoardHistoryCard, AddCommentInfo, GetCommentInfo, EditCommentInfo, NarrativeBoardComment, TeamMember, NarrativeBoardCommentMessageDS } from '../../../modal/narrative-board';
import { connect } from 'react-redux';
import DeleteConfirmationModal from '../../delete-cofirmation-modal/delete-cofirmation-modal';
import { getUserInitials, getUsernameAbbreviation } from '../../../helper/utilities';
import { CONFIG } from '../../../helper/config';
import _ from 'lodash';
import { MentionsInput, Mention } from 'react-mentions';
import { UserConfigModal } from '../../../modal/notification';

interface IProps {
    teams_context: any;
    nb_insight_id: number;
    narrative_board_card: NarrativeBoardHistoryCard | undefined;
    auth_token: string;
    narrative_board_team_members: TeamMember[];
    addComment: (arg0: AddCommentInfo, arg1: GetCommentInfo, arg2: number, auth_token: string) => void;
    editComment: (arg0: EditCommentInfo, arg1: GetCommentInfo, arg2: number, auth_token: string) => void;
    deleteComment: (arg0: number, arg1: UserConfigModal, arg2: GetCommentInfo, arg3: number, auth_token: string) => void;
}

interface IState {
    new_comment_text: string;
    edit_comment_text: string;
    edit_comment_index: string;
    edit_comment_id: number;
    is_follow_comment: boolean;
    orginal_message_id: number;
    open_delete_confirmation_modal: boolean;
    reply_comment_text: string;
    comment_start_index: number;
    comment_end_index: number;
    follow_up_comments: { orginal_message_id: number, start: number, end: number }[];
    is_comment_in_view_all_mode: boolean;
    mentioned_members: TeamMember[];
    mentioned_members_in_reply: TeamMember[];
    mentioned_members_edit_comment: TeamMember[];


}


/**
 * NarrativeBoardComments Component
 *
 * This component is responsible for rendering and managing
 * comments inside the Narrative Board card. It supports:
 *
 * - Adding new comments with mentions
 * - Replying to existing comments
 * - Editing user-owned comments
 * - Deleting user-owned comments with confirmation
 * - Viewing and hiding additional comments/replies
 *
 * It integrates with Redux actions (`addComment`, `editComment`, `deleteComment`)
 * and Semantic UI components for UI rendering.
 */
export class NarrativeBoardComments extends Component<IProps, IState> {
    /** Ref for the textarea input used when writing comments/replies */
    textarea_ref: any;
    constructor(props: IProps) {
        super(props);
        this.state = {
            new_comment_text: '',
            edit_comment_text: '',
            edit_comment_index: '',
            edit_comment_id: -1,
            is_follow_comment: false,
            orginal_message_id: -1,
            open_delete_confirmation_modal: false,
            reply_comment_text: '',
            comment_start_index: 0,
            comment_end_index: this.getCommetEndIndex(),
            follow_up_comments: [],
            is_comment_in_view_all_mode: false,
            mentioned_members: [],
            mentioned_members_in_reply: [],
            mentioned_members_edit_comment: []
        }
        this.textarea_ref = React.createRef();
    }

    /**
     * Updates the state of the component when the comments array changes.
     * It keeps track of the end index of the comment array, and updates it
     * if the comments array changes and the end index is different.
     * This makes sure that the component always shows the correct number of comments.
     * @param prevProps - The previous props of the component
     */
    componentDidUpdate(prevProps: IProps) {
        if (this.props.narrative_board_card && prevProps.narrative_board_card && !_.isEqual(prevProps.narrative_board_card.comments, this.props.narrative_board_card.comments)) {
            let comment_end_index;
            if (this.state.is_comment_in_view_all_mode) {
                comment_end_index = this.props.narrative_board_card.comments.length;
            } else {
                comment_end_index = this.getCommetEndIndex();
            }
            if (this.state.comment_end_index !== comment_end_index) {
                this.setState({
                    comment_end_index: comment_end_index,
                });
            }
        }
    }

    /**
     * Extracts plain text content from a comment message array.
     * @param comment - Array of comment message objects
     * @returns Concatenated text content
     */
    getCommentAsString = (comment: NarrativeBoardCommentMessageDS[]) => {
        let text = '';
        comment.forEach(cmt => {
            if (cmt.type === CONFIG.COMMENTTYPE.TEXT) {
                text = text.concat(cmt.value);
            }
        })
        return text;
    }

    /**
     * This function is used to set the edit comment index and text.
     * It checks if the user id is the same as the current user id.
     * If it is, it sets the edit comment index and text.
     * @param user_id - The user id of the comment
     * @param comment_index - The index of the comment
     * @param comment_id - The id of the comment
     * @param comment - The comment array
     */
    setEditCommentIndex = (user_id: string, comment_index: string, comment_id: number, comment: NarrativeBoardCommentMessageDS[]) => {
        let { teams_context } = this.props;
        this.cancelAddReplyComment();
        if (teams_context.user_id === user_id) {
            if (this.state.edit_comment_index !== comment_index) {
                let comment_text = this.getCommentAsString(comment);
                this.setState({ edit_comment_index: comment_index, edit_comment_id: comment_id, edit_comment_text: comment_text });
            }
        } else {
            alert(NARRATIVEBOARDCONFIG.COMMENT.EDITWARNING);
        }
    }

    /**
     * This function is used to add a reply comment.
     * It checks if the original message id is different from the current state.
     * If it is, it sets the original message id and reply comment text.
     * If it is not, it focuses on the textarea and sets the reply comment text to empty.
     * @param orginal_message_id - The original message id
     */
    addReplyComment = (orginal_message_id: number) => {
        this.cancelEditComment();
        if (orginal_message_id !== this.state.orginal_message_id) {
            this.setState({ is_follow_comment: true, orginal_message_id: orginal_message_id, reply_comment_text: '' });
        } else {
            if (this.textarea_ref.current) {
                this.textarea_ref.current.focus();
            }
            this.setState({ reply_comment_text: '' });
        }
    }

    /**
     * This function is used to cancel the add reply comment.
     * It sets the original message id to -1 and the reply comment text to empty.
     */
    cancelAddReplyComment = () => {
        if (this.state.orginal_message_id !== -1) {
            this.setState({ is_follow_comment: false, orginal_message_id: -1, reply_comment_text: '', mentioned_members_in_reply: [] });
        }
    }

    /**
     * This function is used to add a comment.
     * It checks if the narrative board card is defined and if the new comment text is not empty.
     * If it is, it sets the new comment text to empty and the mentioned members to empty.
     * It then creates an add comment info object and a get comment info object.
     * It calls the add comment function with the add comment info, get comment info, insight id and auth token.
     */
    addComment = () => {
        let { is_follow_comment, new_comment_text, orginal_message_id, reply_comment_text, mentioned_members, mentioned_members_in_reply } = this.state;
        let { narrative_board_card, teams_context, nb_insight_id, auth_token } = this.props;
        if (narrative_board_card) {
            if (new_comment_text.trim() !== '' || (is_follow_comment && reply_comment_text.trim() !== '')) {

                let mention_array: TeamMember[] = [];
                let comment_text = '', original_message_id = -1;
                if (is_follow_comment) {
                    mention_array = mentioned_members_in_reply;
                    comment_text = reply_comment_text;
                    original_message_id = orginal_message_id;
                } else {
                    mention_array = mentioned_members;
                    comment_text = new_comment_text;
                }

                let add_comment_info: AddCommentInfo = {
                    user_id: teams_context.user_id,
                    user_name: teams_context.userPrincipalName,
                    user_email: teams_context.userPrincipalName,
                    teams_channel_id: teams_context.teamId,
                    tenant_id: teams_context.tid,
                    original_message_id: original_message_id,
                    insight_id: narrative_board_card.insight_id,
                    message: [{
                        type: CONFIG.COMMENTTYPE.TEXT,
                        value: comment_text,
                    },
                    {
                        type: CONFIG.COMMENTTYPE.USERTAG,
                        value: this.mentionedUsersInComment(mention_array, comment_text),
                    }
                    ],
                    sequence_id: narrative_board_card.sequence_id,
                    channelId: teams_context.channelId,
                    team_id: (teams_context.team_id) ? teams_context.team_id : ''
                };

                let get_comment_info: GetCommentInfo = {
                    insight_id: narrative_board_card.insight_id,
                    sequence_id: narrative_board_card.sequence_id,
                    tenant_id: teams_context.tid,
                    entityId: teams_context.entityId,
                    teams_channel_id: teams_context.teamId,
                    team_id: (teams_context.team_id) ? teams_context.team_id : '',
                    channelId: teams_context.channelId,
                    user_id: teams_context.user_id
                };

                this.props.addComment(add_comment_info, get_comment_info, nb_insight_id, auth_token);

                if (is_follow_comment) {
                    this.cancelAddReplyComment();
                } else {
                    this.setState({ new_comment_text: '', mentioned_members: [] });
                }
            }
        }
    }

    /**
     * This function is used to edit a comment.
     * It checks if the narrative board card is defined and if the edit comment text is not empty.
     * If it is, it sets the edit comment text to empty and the mentioned members to empty.
     * It then creates an edit comment info object and a get comment info object.
     * It calls the edit comment function with the edit comment info, get comment info, insight id and auth token.
     */
    editComment = (user_id: string) => {
        let { edit_comment_text, edit_comment_id, is_follow_comment, mentioned_members_edit_comment } = this.state;
        let { narrative_board_card, teams_context, nb_insight_id, auth_token } = this.props;

        if (narrative_board_card && edit_comment_text.length > 0) {
            if (teams_context.user_id === user_id) {
                let edit_comment_info: EditCommentInfo = {
                    comment_id: edit_comment_id,
                    hierarchy_id: is_follow_comment ? 2 : 1,
                    message: [
                        {
                            type: CONFIG.COMMENTTYPE.TEXT,
                            value: edit_comment_text
                        },
                        {
                            type: CONFIG.COMMENTTYPE.USERTAG,
                            value: this.mentionedUsersInComment(mentioned_members_edit_comment, edit_comment_text),
                        }
                    ],
                    user_id: teams_context.user_id,
                    teams_channel_id: teams_context.teamsId,
                    entityId: teams_context.entityId,
                    insight_id: narrative_board_card.insight_id,
                    channelId: teams_context.channelId,
                    team_id: (teams_context.team_id) ? teams_context.team_id : '',
                    tenant_id: teams_context.tid,
                };
                let get_comment_info: GetCommentInfo = {
                    insight_id: narrative_board_card.insight_id,
                    sequence_id: narrative_board_card.sequence_id,
                    tenant_id: teams_context.tid,
                    teams_channel_id: teams_context.teamsId,
                    entityId: teams_context.entityId,
                    team_id: (teams_context.team_id) ? teams_context.team_id : '',
                    channelId: teams_context.channelId,
                    user_id: teams_context.user_id
                };
                this.props.editComment(edit_comment_info, get_comment_info, nb_insight_id, auth_token);
                this.cancelEditComment();
            } else {
                alert(NARRATIVEBOARDCONFIG.COMMENT.EDITWARNING);
            }
        }
    }

    /**
     * This function is used to get the mentioned users in a comment.
     * It checks if the mentioned members are in the comment text.
     * If they are, it creates an array of mentioned users and returns it.
     * @param mentioned_members_in_comment - The mentioned members in the comment
     * @param comment_text - The comment text
     * @returns Array of mentioned user metadata
     */
    mentionedUsersInComment = (mentioned_members_in_comment: TeamMember[], comment_text: string) => {
        let { teams_context } = this.props;
        let mentioned_users: any[] = [];
        mentioned_members_in_comment.forEach(member => {
            if (comment_text.includes(member.id)) {
                mentioned_users.push({
                    id: member.id,
                    mail: member.mail,
                    userPrincipalName: member.userPrincipalName,
                    displayName: member.displayName,
                    entityId: teams_context.entityId,
                    teams_channel_id: teams_context.teamId,
                    team_name: teams_context.teamName
                })
            }
        })
        return mentioned_users;
    }

    /**
     * This function is used to cancel the edit comment.
     * It sets the edit comment index to empty, the edit comment id to -1,
     * the edit comment text to empty and the mentioned members to empty.
     */
    cancelEditComment = () => {
        if (this.state.edit_comment_id !== -1) {
            this.setState({ edit_comment_index: "", edit_comment_id: -1, edit_comment_text: '', mentioned_members_edit_comment: [] });
        }
    }

    /**
     * This function is used to delete a comment.
     * It checks if the narrative board card is defined and if the delete comment is true.
     * If it is, it creates a get comment info object and a user config keys object.
     * It calls the delete comment function with the edit comment id, user config keys,
     * get comment info, insight id and auth token.
     * @param is_delete - The boolean value to check if the comment should be deleted
     */
    deleteComment = (is_delete: boolean) => {
        let { narrative_board_card, nb_insight_id, teams_context, auth_token } = this.props;
        let { edit_comment_id } = this.state;
        if (narrative_board_card && is_delete) {
            let get_comment_info: GetCommentInfo = {
                insight_id: narrative_board_card.insight_id,
                sequence_id: narrative_board_card.sequence_id,
                tenant_id: teams_context.tid,
                teams_channel_id: teams_context.teamsId,
                entityId: teams_context.entityId,
                team_id: (teams_context.team_id) ? teams_context.team_id : '',
                channelId: teams_context.channelId,
                user_id: teams_context.user_id
            };

            let user_config_keys: UserConfigModal = {
                tenant_id: teams_context.tid,
                team_id: (teams_context.team_id) ? teams_context.team_id : '',
                channelId: teams_context.channelId,
                user_id: teams_context.user_id
            }
            this.props.deleteComment(edit_comment_id, user_config_keys, get_comment_info, nb_insight_id, auth_token);
        }
        this.setState({ open_delete_confirmation_modal: false });
    }

    /**
     * This function is used to open the delete confirmation modal.
     * It checks if the user id is the same as the current user id.
     * If it is, it sets the open delete confirmation modal to true and the edit comment id to the comment id.
     * If it is not, it shows an alert with a warning message.
     * @param comment_id - The comment id
     * @param user_id - The user id of the comment
     */
    openDeleteConfirmationModal = (comment_id: number, user_id: string) => {
        let { teams_context } = this.props;
        this.cancelEditComment();
        this.cancelAddReplyComment();
        if (teams_context.user_id === user_id) {
            this.setState({ open_delete_confirmation_modal: true, edit_comment_id: comment_id })
        } else {
            alert(NARRATIVEBOARDCONFIG.COMMENT.DELETEWARNING);
        }
    }

    /**
     * This function is used to view the previous comments.
     * It sets the comment start index to 0.
     */
    viewPreviousComments = () => {
        this.setState({ comment_start_index: 0 });
    }

    /**
     * This function is used to view more comments.
     * It checks if the narrative board card is defined and sets the comment end index to the length of the comments array.
     */
    viewMoreComments = () => {
        if (this.props.narrative_board_card) {
            let { comments } = this.props.narrative_board_card;
            this.setState({ comment_end_index: comments.length, is_comment_in_view_all_mode: true });
        }
    }

    /**
     * This function is used to hide the comments.
     * It sets the comment end index to the length of the comments array and the comment start index to 0.
     */
    hideComments = () => {
        let comment_end_index = this.getCommetEndIndex();
        this.setState({ comment_end_index: comment_end_index, comment_start_index: 0, is_comment_in_view_all_mode: false });
    }

    viewPreviousReplies = () => {

    }

    /**
     * This function is used to view more replies.
     * It checks if the original message id is different from the current state.
     * If it is, it sets the original message id and the end index to the length of the comments array.
     * If it is not, it sets the end index to the length of the comments array.
     * @param original_message_id - The original message id
     * @param end - The end index
     */
    viewMoreReplies = (original_message_id: number, end: number) => {
        let { follow_up_comments } = this.state;
        let index = follow_up_comments.findIndex(fc => fc.orginal_message_id === original_message_id);
        if (index === -1) {
            follow_up_comments = follow_up_comments.concat({
                orginal_message_id: original_message_id,
                start: 0, end: end
            });
        } else {
            follow_up_comments[index].end = end;
        }
        this.setState({ follow_up_comments: follow_up_comments });
    }

    /**
     * This function is used to hide more replies.
     * It checks if the original message id is different from the current state.
     * If it is, it sets the original message id and the end index to the length of the comments array.
     * If it is not, it sets the end index to the length of the comments array.
     * @param original_message_id - The original message id
     * @param end - The end index
     */
    hideMoreReplies = (original_message_id: number, end: number) => {
        let { follow_up_comments } = this.state;
        let index = follow_up_comments.findIndex(fc => fc.orginal_message_id === original_message_id);
        if (index === -1) {
            follow_up_comments = follow_up_comments.concat({
                orginal_message_id: original_message_id,
                start: 0, end: end
            });
        } else {
            follow_up_comments[index].end = end;
        }
        this.setState({ follow_up_comments: follow_up_comments });
    }

    /**
     * This function is used to get the comment end index.
     * It checks if the narrative board card is defined and if the comments array is not empty.
     * If it is, it sets the end index to the length of the comments array.
     * If it is not, it sets the end index to -1.
     */
    getCommetEndIndex = () => {
        if (this.props.narrative_board_card) {
            let { comments } = this.props.narrative_board_card;
            let end_index = 0;
            if (comments.length > 0) {
                let last_index = comments.length - 1;
                let last_comment = comments[last_index];
                end_index = (last_comment.follow_up_messages && last_comment.follow_up_messages.length > 0) ? 1 : 2;
                end_index = end_index > comments.length ? comments.length : end_index;
            }
            return end_index;
        } else {
            return -1;
        }
    }

    /**
     * This function is used to check if the comments should be hidden.
     * It checks if the narrative board card is defined and if the comments array is not empty.
     * If it is, it checks if the comment start index is 0 and the comment end index is the length of the comments array.
     * If it is, it returns true.
     * If it is not, it returns false.
     */
    isHideComments = () => {
        if (this.props.narrative_board_card) {
            let { comments } = this.props.narrative_board_card;
            let { comment_start_index, comment_end_index } = this.state;
            let is_hide = false;
            if (comments.length > 0) {
                let last_index = comments.length - 1;
                let last_comment = comments[last_index];
                if ((last_comment.follow_up_messages && last_comment.follow_up_messages.length > 0)) {
                    is_hide = comments.length > 1 && comment_start_index === 0 && comment_end_index === comments.length;
                } else {
                    is_hide = comments.length > 2 && comment_start_index === 0 && comment_end_index === comments.length;
                }
            }
            return is_hide;
        }
    }

    /**
     * This function is used to render the suggestions in the mentions input.
     * It creates a header with the user initials and the user name.
     * @param suggestion - The suggestion object
     * @returns The header JSX Element with the user initials and the user name
     */
    renderSuggestions = (suggestion) => {
        return <Header as='h4' className="user">
            <Label circular as='a' size="large">{getUserInitials(suggestion.display)}</Label>
            <Header.Content style={{ marginLeft: "15px", marginTop: "-10px" }} >{suggestion.display}
                <Header.Subheader content={suggestion.userPrincipalName} />
            </Header.Content></Header>
    }

    /**
     * This function is used to render the add new reply message.
     * It creates a mentions input with a placeholder and a button to save the comment.
     * @returns The comment group with the mentions input and buttons
     */
    renderAddNewReplyMessage = () => {
        let { reply_comment_text } = this.state;
        const newArrayOfObj = this.props.narrative_board_team_members.map(({
            displayName: display,
            ...rest
        }) => ({
            display,
            ...rest
        }));
        return (<Comment.Group style={{ minWidth: '98%' }}>
            <Comment>
                <Comment.Content>
                    <Form.Group inline className='comment-form-group reply-comment'>
                        <MentionsInput
                            className="mentions reply-comment"
                            singleLine
                            allowSpaceInQuery={true}
                            autoComplete="off"
                            allowSuggestionsAboveCursor={true}
                            placeholder={NARRATIVEBOARDCONFIG.INPUT.COMMENTPLACEHOLDER}
                            value={reply_comment_text}
                            name='reply_comment_text'
                            onChange={this.onChangeForReply}
                            onKeyDown={(event: any) => (event.key === "Enter") ? this.addComment() : null}
                        >
                            <Mention
                                className="mentions__mention"
                                markup={CONFIG.COMMENT_MENTION_MARKUP}
                                type="user"
                                onAdd={this.onAdd}
                                trigger="@"
                                data={newArrayOfObj}
                                appendSpaceOnAdd={true}
                                renderSuggestion={suggestion => this.renderSuggestions(suggestion)}
                            />
                        </MentionsInput>
                        <Button style={{ marginRight: '10px', marginLeft: "5px" }} className='add-comment' circular icon='save' size='mini'
                            disabled={reply_comment_text.trim() === ''}
                            onClick={() => this.addComment()} />
                        <Button className='add-comment' circular icon='close' size='mini'
                            onClick={() => this.cancelAddReplyComment()} />
                    </Form.Group>
                </Comment.Content>
            </Comment>
        </Comment.Group>)
    }

    /**
     * This function is used to render the comment content.
     * It creates a popup with the user name and a button to delete the comment.
     * @param comment - The comment object
     * @param comment_index - The index of the comment
     * @param is_follow_message - The boolean value to check if the comment is a follow message
     * @returns The comment content JSX Element with the user name and delete button
     */
    renderCommentContent = (comment: NarrativeBoardComment, comment_index: string, is_follow_message: boolean) => {
        let { edit_comment_index, edit_comment_text } = this.state;
        const newArrayOfObj = this.props.narrative_board_team_members.map(({
            displayName: display,
            ...rest
        }) => ({
            display,
            ...rest
        }));

        return <>
            <Popup trigger={<Comment.Avatar as={Label} circular content={getUsernameAbbreviation(comment.user_name)} />}
                on='hover' content={comment.user_name} position='right center' basic />

            <Comment.Content>
                <Comment.Text style={{ width: (edit_comment_index === comment_index) ? '100%' : 'fit-content' }}
                    onClick={() => { this.setEditCommentIndex(comment.user_id, comment_index, comment.comment_id, comment.message) }}
                    content={(edit_comment_index === comment_index) ? <Form unstackable={true}>
                        <Form.Group inline className='comment-form-group'>

                            <MentionsInput
                                className="mentions edit-comment"
                                singleLine
                                allowSpaceInQuery={true}
                                autoComplete="off"
                                allowSuggestionsAboveCursor={true}
                                placeholder={NARRATIVEBOARDCONFIG.INPUT.COMMENTPLACEHOLDER}
                                value={edit_comment_text}
                                name='edit_comment_text'
                                onChange={this.onChangeForText}
                                onKeyDown={(event: any) => (event.key === "Enter") ? this.editComment(comment.user_id) : null}
                            >
                                <Mention
                                    className="mentions__mention"
                                    markup={CONFIG.COMMENT_MENTION_MARKUP}
                                    type="user"
                                    onAdd={this.onAdd}
                                    trigger="@"
                                    data={newArrayOfObj}
                                    appendSpaceOnAdd={true}
                                    renderSuggestion={suggestion => this.renderSuggestions(suggestion)}
                                />
                            </MentionsInput>
                            <Button style={{ marginRight: '10px', marginLeft: "5px" }} className='add-comment' circular icon='save' size='mini'
                                disabled={edit_comment_text.length === 0}
                                onClick={() => this.editComment(comment.user_id)} />
                            <Button className='add-comment' circular icon='close' size='mini'
                                onClick={() => this.cancelEditComment()} />
                        </Form.Group>
                    </Form> : <div>
                        {comment.message.map((msg, i) => {
                            let new_msg = ''
                            if (msg.type === CONFIG.COMMENTTYPE.TEXT) {
                                new_msg = msg.value;
                                new_msg = new_msg.split("@{^@{").join("<strong id='");
                                new_msg = new_msg.split("|^@^|").join("'>");
                                new_msg = new_msg.split("}^@^}").join("</strong>")
                            }
                            return <React.Fragment key={new_msg} >
                                <span dangerouslySetInnerHTML={{ __html: new_msg }}></span>
                            </React.Fragment>
                        })}
                    </div>
                    } />
                <Comment.Actions>
                    {(!is_follow_message) ? <Comment.Action onClick={() => this.addReplyComment(comment.message_id)} >
                        {NARRATIVEBOARDCONFIG.BUTTONTEXT.REPLY}</Comment.Action> : null}
                    <Comment.Action onClick={() => this.openDeleteConfirmationModal(comment.comment_id, comment.user_id)} >{NARRATIVEBOARDCONFIG.BUTTONTEXT.DELETE}</Comment.Action>
                </Comment.Actions>
            </Comment.Content>
        </>
    }

    /**
     * This function is used to render the child comments.
     * It creates a comment group with the child comments and a button to view more replies.
     * @param comments - The comments array
     * @param original_message_id - The original message id
     * @returns The comment group JSX Element with the child comments and view more replies button
     */
    renderChildComments = (comments: NarrativeBoardComment[], original_message_id: number) => {
        let { follow_up_comments } = this.state;
        comments = comments.slice().reverse();
        let start = 0;
        let end = (comments.length > CONFIG.MINREPLYCOMMENTLENGTH) ? CONFIG.MINREPLYCOMMENTLENGTH : comments.length;
        if (comments.length > CONFIG.MINREPLYCOMMENTLENGTH) {
            let index = follow_up_comments.findIndex(fc => fc.orginal_message_id === original_message_id);
            if (index !== -1) {
                start = follow_up_comments[index].start;
                end = follow_up_comments[index].end;
            }
        }

        return (<Comment.Group style={{ minWidth: '98%' }}>
            {(start !== 0) ? <Button className='link-button' basic primary
                content={NARRATIVEBOARDCONFIG.COMMENT.VIEWMORECOMMENTS}
                onClick={() => this.viewPreviousReplies()} /> : null}
            {
                comments.slice(start, end).map((comment, i) => {
                    let comment_index = `followupcomment${i}${comment.message_id}${comment.comment_id}`
                    return (<Comment key={comment.message_id}>
                        {this.renderCommentContent(comment, comment_index, true)}
                    </Comment>)
                })
            }
            {(end !== comments.length) ? <Button className='link-button' basic primary
                content={NARRATIVEBOARDCONFIG.COMMENT.VIEWMOREREPLIES}
                onClick={() => this.viewMoreReplies(original_message_id, comments.length)} /> : null}

            {(comments.length > CONFIG.MINREPLYCOMMENTLENGTH && start === 0 && end === comments.length) ? <Button className='link-button' basic primary
                content={NARRATIVEBOARDCONFIG.COMMENT.HIDEMOREREPLIES}
                onClick={() => this.hideMoreReplies(original_message_id, CONFIG.MINREPLYCOMMENTLENGTH)} /> : null}
        </Comment.Group>)
    }

    /**
     * 
     * @param ev - Event object
     * @param newValue - new comment text value
     * This function is used to handle the on change event of the mentions input.
     * It sets the new comment text to the value of the input.
     */
    onChange = (ev, newValue) => {
        this.setState({ new_comment_text: newValue })
    }

    /**
     * 
     * @param ev - Event object
     * @param newValue - new edit comment text value
     * This function is used to handle the on change event of the mentions input.
     * It sets the edit comment text to the value of the input.
     */
    onChangeForText = (ev, newValue) => {
        this.setState({ edit_comment_text: newValue })
    }

    /**
     * 
     * @param ev - Event object
     * @param newValue - new reply comment text value
     * This function is used to handle the on change event of the mentions input.
     * It sets the reply comment text to the value of the input.
     */
    onChangeForReply = (ev, newValue) => {
        this.setState({ reply_comment_text: newValue })
    }

    /**
     * This function is used to handle the on add event of the mentions input.
     * It checks if the user is already mentioned in the comment.
     * If it is, it adds the user to the mentioned members array.
     * @param id - The id of the user
     */
    onAdd = (id: string) => {
        let { is_follow_comment, edit_comment_id, mentioned_members_in_reply,
            mentioned_members_edit_comment, mentioned_members } = this.state;
        let { narrative_board_team_members } = this.props;
        let new_user = narrative_board_team_members.find(member => member.id === id);
        if (new_user) {
            if (is_follow_comment && !mentioned_members_in_reply.find(member => member.id === id)) {
                this.setState({ mentioned_members_in_reply: mentioned_members_in_reply.concat(new_user) });
            } else if (edit_comment_id !== -1 && !mentioned_members_edit_comment.find(member => member.id === id)) {
                this.setState({ mentioned_members_edit_comment: mentioned_members_edit_comment.concat(new_user) });
            } else if (!mentioned_members.find(member => member.id === id)) {
                this.setState({ mentioned_members: mentioned_members.concat(new_user) });
            }
        }
    }

    /**
     * Renders the NarrativeBoardComments component.
     * 
     * This method checks if the `narrative_board_card` prop is available,
     * then processes and displays the list of comments along with relevant 
     * buttons for viewing more or fewer comments, hiding comments, and adding new comments. 
     * 
     * A `MentionsInput` component is used to allow users to add new comments with 
     * mentions support. The component also handles rendering the delete confirmation 
     * modal if needed.
     * 
     * The comments are sliced and reversed for display, and support is included for 
     * rendering child comments and follow-up messages.
     * 
     * Returns a JSX element containing the comments and inputs or an empty fragment.
     */
    render() {
        if (this.props.narrative_board_card) {
            let { new_comment_text, open_delete_confirmation_modal, orginal_message_id, is_follow_comment,
                comment_end_index, comment_start_index } = this.state;
            let show_all_comments = '';
            let { comments } = this.props.narrative_board_card;
            comments = comments.slice().reverse();
            let is_hide_comment = this.isHideComments()
            const newArrayOfObj = this.props.narrative_board_team_members.map(({
                displayName: display,
                ...rest
            }) => ({
                display,
                ...rest
            }));

            return (<>
                <Comment.Group className={`narrative-board-comment-group ${show_all_comments}`}>
                    {(comment_start_index !== 0) ? <Button className='link-button' primary basic
                        content={NARRATIVEBOARDCONFIG.COMMENT.VIEWPREVIOUSCOMMENTS}
                        onClick={() => this.viewPreviousComments()} />
                        : null}
                    {
                        comments.slice(comment_start_index, comment_end_index).map((comment, i) => {
                            let comment_index = `parentcomment${i}${comment.message_id}${comment.comment_id}`
                            return (<Comment key={comment.message_id}>
                                {this.renderCommentContent(comment, comment_index, false)}
                                {(is_follow_comment && orginal_message_id === comment.message_id) ?
                                    this.renderAddNewReplyMessage() : null}
                                {((comment.follow_up_messages && comment.follow_up_messages.length > 0)) ? this.renderChildComments(comment.follow_up_messages, comment.message_id) : null}
                            </Comment>)
                        })
                    }
                    {(comment_end_index !== comments.length) ? <Button className='link-button' basic primary
                        content={NARRATIVEBOARDCONFIG.COMMENT.VIEWMORECOMMENTS}
                        onClick={() => this.viewMoreComments()} />
                        : null}

                    {(is_hide_comment) ? <Button className='link-button' basic primary
                        content={NARRATIVEBOARDCONFIG.COMMENT.HIDEMORECOMMENTS}
                        onClick={() => this.hideComments()} />
                        : null}
                </Comment.Group>
                <div className="add-new-comment-text-area">
                    <MentionsInput
                        singleLine
                        className="mentions"
                        allowSpaceInQuery={true}
                        autoComplete="off"
                        allowSuggestionsAboveCursor={true}
                        placeholder={NARRATIVEBOARDCONFIG.INPUT.COMMENTPLACEHOLDER}
                        value={new_comment_text}
                        name='new_comment_text'
                        onChange={this.onChange}
                        onFocus={() => { this.cancelEditComment(); this.cancelAddReplyComment(); }}
                        onKeyDown={(event: any) => (event.key === "Enter") ? this.addComment() : null}
                    >
                        <Mention
                            className="mentions__mention"
                            markup={CONFIG.COMMENT_MENTION_MARKUP}
                            type="user"
                            onAdd={this.onAdd}
                            trigger="@"
                            data={newArrayOfObj}
                            appendSpaceOnAdd={true}
                            renderSuggestion={(suggestion) => {
                                return <Header as='h4' className="user">
                                    <Label circular as='a' size="large">{getUserInitials(suggestion.display)}</Label>
                                    <Header.Content style={{ marginLeft: "15px", marginTop: "-10px" }} >{suggestion.display}
                                        <Header.Subheader content={suggestion.userPrincipalName} />
                                    </Header.Content></Header>
                            }}
                        />
                    </MentionsInput>

                    <Button className='add-comment' circular icon='send' size='mini' disabled={new_comment_text.length === 0}
                        onClick={() => this.addComment()} style={{ marginLeft: "5px" }} />
                </div>
                {(open_delete_confirmation_modal) ? <DeleteConfirmationModal open={open_delete_confirmation_modal}
                    content={NARRATIVEBOARDCONFIG.DELETEMODELCONTENT}
                    close={this.deleteComment} /> : null}
            </>)
        } else {
            return <></>
        }
    }
}

const mapStateToProps = () => ({
});


export default connect(mapStateToProps, { addComment, editComment, deleteComment })(NarrativeBoardComments);